/**
 * Dayjs Library und Plugins
 * Wird nur von anderen modules importiert!
 */

import dayjs from 'dayjs';
import de from 'dayjs/locale/de.js';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import isoWeek from 'dayjs/plugin/isoWeek.js';
import isLeapYear from 'dayjs/plugin/isLeapYear.js';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear.js';
import weekOfYear from 'dayjs/plugin/weekOfYear.js';
import duration from 'dayjs/plugin/duration.js';
import isBetween from 'dayjs/plugin/isBetween.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import objectSupport from 'dayjs/plugin/objectSupport.js';
import minMax from 'dayjs/plugin/minMax.js';

dayjs.locale(de); // use locale globally
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(isLeapYear);
dayjs.extend(isoWeeksInYear);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(objectSupport);
dayjs.extend(minMax);
dayjs.tz.setDefault('Europe/Berlin');
dayjs.tz.DEFAULT_TZ = 'Europe/Berlin';


/**
 * Einfacher Vergleich zwischen zwei HH:mm Strings
 * @param {*} option 
 * @param {*} Dayjs 
 * @param {*} dayjsFactory 
 */
function durationPlugin(option, Dayjs, dayjsFactory) {
  dayjsFactory.getDurationHHmm = function(fromStr, toStr) {
    const [fromH, fromM] = fromStr.split(':').map(Number);
    const [toH, toM] = toStr.split(':').map(Number);

    const fromMinutes = fromH * 60 + fromM;
    const toMinutes = toH * 60 + toM;

    let diff = toMinutes - fromMinutes;
    if (diff < 0) diff += 24 * 60; // handle overnight

    return diff;
  };
}


/**
 * Day.js plugin that adds a helper to compare two "HH:mm" strings.
 *
 * Usage:
 *   dayjs.isAfterTimeHHmm("17:30", "09:00"); // true
 *   dayjs.isAfterTimeHHmm("07:00", "09:00"); // false
 *
 * Note:
 * - This does NOT handle overnight wrap-around.
 *   "01:00" compared to "23:00" → false (01:00 is not after 23:00).
 *   If you need overnight logic, we can extend it.
 *
 * @param {string} a - First time in "HH:mm" format
 * @param {string} b - Second time in "HH:mm" format
 * @returns {boolean} true if a > b
 */
function compareTimesPlugin(option, Dayjs, dayjsFactory) {
  dayjsFactory.isAfterTimeHHmm = function(a, b) {
    const [aH, aM] = a.split(':').map(Number);
    const [bH, bM] = b.split(':').map(Number);

    const aMinutes = aH * 60 + aM;
    const bMinutes = bH * 60 + bM;

    return aMinutes > bMinutes;
  };
  dayjsFactory.isSameOrAfterTimeHHmm = function(a, b) {
    const [aH, aM] = a.split(':').map(Number);
    const [bH, bM] = b.split(':').map(Number);

    const aMinutes = aH * 60 + aM;
    const bMinutes = bH * 60 + bM;

    return aMinutes >= bMinutes;
  };
  dayjsFactory.isBeforeTimeHHmm = function(a, b) {
    const [aH, aM] = a.split(':').map(Number);
    const [bH, bM] = b.split(':').map(Number);

    const aMinutes = aH * 60 + aM;
    const bMinutes = bH * 60 + bM;

    return aMinutes < bMinutes;
  };
  dayjsFactory.isTimeInRangeHHmm = function(testStr, fromStr, toStr) {
    const toMinutes = s => {
      const [h, m] = s.split(':').map(Number);
      return h * 60 + m;
    };

    const test = toMinutes(testStr);
    const from = toMinutes(fromStr);
    const to = toMinutes(toStr);

    if (from <= to) {
      // Normal same-day range
      return test >= from && test <= to;
    } else {
      // Overnight range
      return test >= from || test <= to;
    }
  };
}

dayjs.extend(compareTimesPlugin);

dayjs.extend(durationPlugin);

export default dayjs;
